import Color from '@/Enums/Color.enum';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import PackagesSection from '@/components/PackagesSection';
import SearchList from '@/components/SearchList';
import { useAppSelector } from '@/store/hooks';
import { selectActiveCampaignLoading } from '@/store/selectors/campaignManagement.selectors';
import { selectSharedApiDealGroupControlOptions, selectSharedApiPackages } from '@/store/selectors/sharedApi.selectors';
import { Box, FormLabel } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  index: number;
}

const controlName = 'dealGroups';
const packageControlName = 'packageId';

const DealGroupsSection = ({ index }: IProps) => {
  const loadingCampaign = useAppSelector(selectActiveCampaignLoading);
  const { setValue, control } = useFormContext();

  const value = useWatch({
    control,
    name: controlName,
  });

  const id = useMemo(() => `line-item-deal-groups${index}`, [index]);
  const { dealGroups: options, loading: dealGroupsLoading } = useAppSelector(state =>
    selectSharedApiDealGroupControlOptions(state),
  );
  const { packages } = useAppSelector(selectSharedApiPackages);
  const hasPackages = useMemo<boolean>(() => !!packages?.length, [packages?.length]);

  const loading = useMemo(() => loadingCampaign || dealGroupsLoading, [loadingCampaign, dealGroupsLoading]);

  //#region useCallback
  const setFormValue = useCallback(
    (values: IControlOption[]) => {
      setValue(controlName, values, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );
  //#endregion

  if (hasPackages) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <PackagesSection fieldName={packageControlName} id={id} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <FormLabel
        htmlFor={id}
        sx={{ marginBottom: '12px', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center' }}
      >
        <span>Deal Groups</span>
      </FormLabel>
      <SearchList
        id={id}
        sx={{
          borderWidth: '1px',
          borderRadius: '8px',
          borderColor: Color.GreyBorder,
          maxHeight: '650px',
          flex: 1,
        }}
        label='Deal Group'
        onChange={setFormValue}
        options={options}
        value={value}
        disabled={loading}
      />
    </Box>
  );
};

export default DealGroupsSection;
